import React, { useState } from 'react';
import { hot } from 'react-hot-loader';
import { connect } from 'react-redux';
import * as Sentry from '@sentry/browser';
import Button from '../Elements/Button/Button';
import Checkbox from '../Elements/Checkbox/Checkbox';
import Input from '../Elements/Input/Input';
import { subscribeToNewsletter } from '../../actions/checkout';
import { purifier } from '../../utils/dom';
import { translate } from '../../utils/locale';
import { generateEventId, fieldValidation } from '../../utils/string';
import s from './Newsletter.scss';

const Newsletter = ({ setup, footer, isPopup = false, isBlock = false, callback = false, terms = false, requiredFields = ['email'], optionalFields = [], lead = false, leadValue, ...props }) => {
	const [fields, updateFields] = useState({});
	const [errors, updateErrors] = useState({});
	const [alreadyMember, setAlreadyMember] = useState(false);
	const [isRequiredFields, setRequiredFields] = useState(requiredFields);
	const [isOptionalFields, setOptionalFields] = useState(optionalFields);
	const [sent, updateSent] = useState(false);
	const [error, updateError] = useState(false);

	const newsletterInputChangeHandler = (id, value) => {
		const field = id.replace('newsletterFooter_', '');
		updateFields({ ...fields, [field]: value });
		updateError(false);
	};

	const newsletterInputFocusHandler = () => {
		updateError(false);
	};

	const newsletterTeamTermsHandler = () => {
		updateFields({ ...fields, teamTerms: !fields.teamTerms });
	};

	const submitNewsletterHandler = (e) => {
		e.preventDefault();

		if (isRequiredFields.length > 0) {
			const submittedErrors = isRequiredFields.filter((f) => fieldValidation(fields[f], f)).map((f) => ({ label: f, error: fieldValidation(fields[f], f) }));

			updateErrors(submittedErrors.reduce((acc, f) => ({ ...acc, [f.label]: translate(setup, `error${f.error}`) }), {}));

			if (submittedErrors.length > 0) {
				updateError(true);
				return;
			}
		}

		const { team, teamTerms, ...otherFields } = fields;
		const properties = { team, campaign: window.location.pathname.replace('/', '') };
		if (teamTerms) properties.team_terms = 1;

		props.subscribeToNewsletter({ ...otherFields, properties }, requiredFields.length > 1)
			.then(() => {
				if (callback) callback();

				if (lead) {
					const uniqueEventID = generateEventId();
					window.dataLayer = window.dataLayer || [];
					window.dataLayer.push({
						event: 'lead',
						event_id: uniqueEventID,
						lead: true,
						values: {
							...leadValue,
						},
					});
				}

				window.setTimeout(() => {
					updateSent(false);
				}, 3000);

				updateFields({});
				updateSent(true);

				return true;
			})
			.catch((err) => {
				Sentry.captureException(err);
				updateError(true);
				return false;
			});
	};

	const toggleMemberStatusHandler = () => {
		setAlreadyMember((prevState) => {
			const isAMember = !prevState;

			if (isAMember) {
				setRequiredFields(['email']);
				setOptionalFields([]);
			} else {
				setRequiredFields(requiredFields);
				setOptionalFields(optionalFields);
			}

			return isAMember;
		});
	};

	return (
		<div className={[s.newsletter, (isBlock ? s.newsletter__block : null), (isPopup ? s.newsletter__popup : null), (sent ? s.newsletter__sent : null)].join(' ')}>
			<form className={[s.newsletter_form, (error ? s.newsletter_form__error : null)].join(' ')} onSubmit={submitNewsletterHandler}>
				{
					(isRequiredFields.indexOf('firstName') !== -1 || isOptionalFields.indexOf('firstName') !== -1)
						&& <Input id="newsletterFooter_firstName" label={translate(setup, 'firstNameLabel')} type="text" name="firstName" value={fields.firstName} isError={errors.firstName} inputFocus={newsletterInputFocusHandler} inputChange={newsletterInputChangeHandler} />
				}
				{
					(isRequiredFields.indexOf('lastName') !== -1 || isOptionalFields.indexOf('lastName') !== -1)
						&& <Input id="newsletterFooter_lastName" label={translate(setup, 'lastNameLabel')} type="text" name="lastName" value={fields.lastName} isError={errors.lastName} inputFocus={newsletterInputFocusHandler} inputChange={newsletterInputChangeHandler} />
				}
				{
					(isRequiredFields.indexOf('team') !== -1 || isOptionalFields.indexOf('team') !== -1)
						&& <Input id="newsletterFooter_team" label={translate(setup, 'teamLabel')} type="text" name="team" value={fields.team} isError={errors.team} inputFocus={newsletterInputFocusHandler} inputChange={newsletterInputChangeHandler} />
				}
				{
					(isRequiredFields.indexOf('email') !== -1 || isOptionalFields.indexOf('email') !== -1)
						&& <Input id="newsletterFooter_email" label={isBlock ? translate(setup, 'emailLabel') : null} type="email" name="email" value={fields.email} isError={errors.email} inputFocus={newsletterInputFocusHandler} inputChange={newsletterInputChangeHandler} />
				}
				{
					(isRequiredFields.indexOf('teamTerms') !== -1 || isOptionalFields.indexOf('teamTerms') !== -1)
						&& <Checkbox id="newsletterFooter_teamTerms" innerHTML={translate(setup, 'teamTerms')} name="teamTerms" selected={fields.teamTerms} inputChange={newsletterTeamTermsHandler} />
				}
				<Button type="submit" text={translate(setup, alreadyMember ? 'newsletterSignIn' : footer.newsletterButton)} />
				<span className={s.newsletter_form_thanks}>{translate(setup, footer.newsletterThanks)}</span>
				{ requiredFields.length > 1 && alreadyMember && <Button variantClasses={['button__transparent', 'button__secondary']} type="button" clickEventHandler={toggleMemberStatusHandler}>{translate(setup, 'newsletterNewMember')}</Button> }
				{ requiredFields.length > 1 && !alreadyMember && <Button variantClasses={['button__transparent', 'button__secondary']} type="button" clickEventHandler={toggleMemberStatusHandler}>{translate(setup, 'newsletterExistingMember')}</Button> }
			</form>
			<div className={s.newsletter_terms} dangerouslySetInnerHTML={{ __html: purifier(translate(setup, terms || footer.newsletterTerms)) }} />
		</div>
	);
};


const mapState = ({ environment }, { callback }) => ({ setup: environment.setup, footer: environment.footer, callback });

export default hot(module)(connect(mapState, { subscribeToNewsletter })(Newsletter));
